import React, { useEffect, useRef } from "react";

import InputSelect from "../InputSelect";
import api from "../../services/api";
import InstallmentsEditor from "./InstallmentsEditor";
import usePaymentOptions from "./usePaymentOptions";
import useNewPaymentTerm from "./useNewPaymentTerm";
import { labelForInstallments } from "./helpers";

const CUSTOM = 'custom';
const LEGACY = 'legacy';

const isEmptyPayment = value => !value.payment_term_id && !value.payment_installments && !value.payment_term
    && !value.payment_method_id && !value.payment_form;

/**
 * Condição e forma de pagamento de um documento (BDM-1208): escolhe do
 * cadastro, monta uma condição personalizada ou mantém o texto livre que o
 * documento antigo já tinha — esse texto nunca some sozinho, só quando o
 * usuário escolhe outra coisa.
 *
 * `value` tem os campos de paymentFromResource; `change` recebe o objeto
 * inteiro atualizado. Com `companyId`, escolher o cliente/fornecedor num
 * documento ainda sem condição traz o padrão dele. `modalLevel` sobe o modal
 * de "+ Nova condição" quando os campos já estão dentro de um modal.
 */
export const PaymentConditionFields = ({ value, change, usage = 'sale', companyId, showMethod = true, termClassName = 'col-3', methodClassName = 'col-3', menuInBody, modalLevel }) => {
    const { terms, methods, loading, addTerm } = usePaymentOptions(usage);
    const previousCompanyId = useRef(companyId);

    // Texto livre de quando o documento foi aberto, para o usuário poder
    // voltar a ele depois de experimentar outra opção.
    // Guardado durante a renderização, e não num efeito: a opção precisa
    // existir já na primeira tela, senão o select aparece vazio.
    const legacyTerm = useRef(null);
    const legacyForm = useRef(null);

    if (legacyTerm.current === null && !value.payment_term_id && !value.payment_installments && value.payment_term) {
        legacyTerm.current = value.payment_term;
    }
    if (legacyForm.current === null && !value.payment_method_id && value.payment_form) {
        legacyForm.current = value.payment_form;
    }

    // O app roda em ReactDOM.render (legado), que não agrupa os setState de um
    // .then: ao abrir o documento a empresa chega numa renderização e o
    // pagamento salvo só na seguinte. A resposta do padrão é conferida contra
    // o valor de agora, senão sobrescreve a condição que acabou de carregar.
    const latestValue = useRef(value);
    latestValue.current = value;

    useEffect(() => {
        const companyChanged = previousCompanyId.current !== companyId;
        previousCompanyId.current = companyId;

        if (!companyChanged || !companyId || !isEmptyPayment(value)) return;

        api.get(`/payment-term/defaults`, { params: { company_id: companyId, usage } }).then(res => {
            const { term, method } = res.data.resource;
            const current = latestValue.current;
            if ((!term && !method) || !isEmptyPayment(current)) return;

            change({
                ...current,
                ...(term ? {
                    payment_term_id: term.id,
                    payment_term: term.name,
                    payment_installments: term.installments,
                } : {}),
                ...(method ? { payment_method_id: method.id, payment_form: method.name } : {}),
            });
        }).catch(() => null);
    }, [companyId]);

    const termSelection = value.payment_term_id
        ?? (value.payment_installments ? CUSTOM : (value.payment_term ? LEGACY : null));
    const methodSelection = value.payment_method_id ?? (value.payment_form ? LEGACY : null);

    const termOptions = [
        ...terms.map(term => ({ label: term.name, value: term.id })),
        // Vinculada a uma condição que depois foi inativada: continua aparecendo.
        ...(value.payment_term_id && !terms.some(term => term.id === value.payment_term_id)
            ? [{ label: `${value.payment_term} (inativa)`, value: value.payment_term_id }]
            : []),
        { label: 'Personalizada', value: CUSTOM },
        ...(legacyTerm.current ? [{ label: `"${legacyTerm.current}" (texto livre)`, value: LEGACY }] : []),
    ];

    const methodOptions = [
        ...methods.map(method => ({ label: method.name, value: method.id })),
        ...(value.payment_method_id && !methods.some(method => method.id === value.payment_method_id)
            ? [{ label: `${value.payment_form} (inativa)`, value: value.payment_method_id }]
            : []),
        ...(legacyForm.current ? [{ label: `"${legacyForm.current}" (texto livre)`, value: LEGACY }] : []),
    ];

    const changeTerm = selected => {
        if (selected === CUSTOM) {
            const installments = value.payment_installments ?? [{ days: 0, percent: 100 }];
            change({
                ...value,
                payment_term_id: null,
                payment_installments: installments,
                payment_term: labelForInstallments(installments),
            });
            return;
        }

        if (selected === LEGACY) {
            change({ ...value, payment_term_id: null, payment_installments: null, payment_term: legacyTerm.current });
            return;
        }

        const term = terms.find(item => item.id === selected);
        if (term) selectTerm(term);
    };

    const selectTerm = term => {
        const method = !value.payment_method_id && !value.payment_form && term.payment_method_id
            ? methods.find(item => item.id === term.payment_method_id)
            : null;

        change({
            ...value,
            payment_term_id: term.id,
            payment_term: term.name,
            payment_installments: term.installments,
            ...(method ? { payment_method_id: method.id, payment_form: method.name } : {}),
        });
    };

    const newTerm = useNewPaymentTerm({
        usage,
        level: modalLevel,
        created: term => {
            addTerm(term);
            selectTerm(term);
        },
    });

    const changeMethod = selected => {
        if (selected === LEGACY) {
            change({ ...value, payment_method_id: null, payment_form: legacyForm.current });
            return;
        }

        const method = methods.find(item => item.id === selected);
        change({ ...value, payment_method_id: method?.id ?? null, payment_form: method?.name ?? '' });
    };

    const changeInstallments = installments => change({
        ...value,
        payment_installments: installments,
        payment_term: labelForInstallments(installments.filter(row => row.days !== '' && row.percent !== '')),
    });

    return (
        <>
            {newTerm.modal}
            <div className={termClassName}>
                <InputSelect
                    label={`Condição de pagamento`}
                    options={termOptions}
                    value={termSelection}
                    change={changeTerm}
                    loading={loading}
                    menuInBody={menuInBody}
                    {...newTerm.selectProps}
                    hint={termSelection === LEGACY
                        ? 'Texto livre: não calcula as parcelas sozinho. Escolha uma condição para automatizar.'
                        : null}
                />
            </div>
            {showMethod &&
                <div className={methodClassName}>
                    <InputSelect
                        label={`Forma de pagamento`}
                        options={methodOptions}
                        value={methodSelection}
                        change={changeMethod}
                        loading={loading}
                        menuInBody={menuInBody}
                    />
                </div>
            }
            {termSelection === CUSTOM &&
                <div className="col-12">
                    <InstallmentsEditor
                        installments={value.payment_installments}
                        change={changeInstallments}
                    />
                </div>
            }
        </>
    );
};

export default PaymentConditionFields;
